import React, { useState } from 'react'
import { cn } from '../../utils/cn'

export interface Tab {
  id: string
  label: string
  icon?: React.ReactNode
  badge?: string | number
  disabled?: boolean
}

export interface TabsProps {
  /**
   * Tab items
   */
  tabs: Tab[]

  /**
   * Active tab ID
   */
  activeTab?: string

  /**
   * Callback when tab changes
   */
  onChange?: (tabId: string) => void

  /**
   * Default active tab (for uncontrolled)
   */
  defaultTab?: string

  /**
   * Variant
   */
  variant?: 'default' | 'pills'

  /**
   * Custom className
   */
  className?: string
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab: controlledActiveTab,
  onChange,
  defaultTab,
  variant = 'default',
  className,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(
    defaultTab || tabs[0]?.id
  )

  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab
  const isControlled = controlledActiveTab !== undefined

  const handleTabClick = (tabId: string, disabled?: boolean) => {
    if (disabled) return

    if (isControlled) {
      onChange?.(tabId)
    } else {
      setInternalActiveTab(tabId)
      onChange?.(tabId)
    }
  }

  return (
    <div className={cn('flex gap-1 overflow-x-auto pb-2', className)} role="tablist">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id

        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            aria-disabled={tab.disabled}
            onClick={() => handleTabClick(tab.id, tab.disabled)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium',
              'transition-colors whitespace-nowrap',
              'focus:outline-none focus:ring-2 focus:ring-toast-gold focus:ring-offset-2',

              // Variant: default
              variant === 'default' && [
                'rounded-t-lg border-b-2',
                isActive ? [
                  'border-toast-gold text-toast-gold bg-toast-gold/5',
                ] : [
                  'border-transparent text-deep-charcoal/70',
                  'hover:text-deep-charcoal hover:bg-butter-cream/50',
                ],
              ],

              // Variant: pills
              variant === 'pills' && [
                'rounded-lg',
                isActive ? [
                  'bg-toast-gold text-white',
                ] : [
                  'bg-butter-cream text-deep-charcoal/70',
                  'hover:bg-butter-cream/70 hover:text-deep-charcoal',
                ],
              ],

              // Disabled
              tab.disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
            )}
          >
            {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.badge && (
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded text-xs',
                  isActive ? 'bg-white/20' : 'bg-soft-tan/50 text-deep-charcoal'
                )}
              >
                {tab.badge}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

Tabs.displayName = 'Tabs'

export default Tabs
